/**
 * Multi-currency subscription pricing configuration
 * Centralized pricing for consistency across all components
 */

export const CURRENCY_CONFIG = {
  INR: { symbol: '₹', subscription: 149, tipPresets: [50, 100, 200], tipMin: 50, tipMax: 5000 },
  CAD: { symbol: 'C$', subscription: 6.95, tipPresets: [3, 5, 10], tipMin: 3, tipMax: 1000 },
  USD: { symbol: '$', subscription: 4.95, tipPresets: [3, 5, 10], tipMin: 3, tipMax: 1000 },
  AED: { symbol: 'AED ', subscription: 18.0, tipPresets: [10, 20, 50], tipMin: 10, tipMax: 3000 },
  AUD: { symbol: 'A$', subscription: 7.50, tipPresets: [5, 10, 20], tipMin: 5, tipMax: 1500 },
  EUR: { symbol: '€', subscription: 4.50, tipPresets: [3, 5, 10], tipMin: 3, tipMax: 1000 },
  GBP: { symbol: '£', subscription: 3.95, tipPresets: [3, 5, 10], tipMin: 3, tipMax: 1000 },
  SGD: { symbol: 'S$', subscription: 6.50, tipPresets: [5, 10, 20], tipMin: 5, tipMax: 1500 },
  JPY: { symbol: '¥', subscription: 750, tipPresets: [500, 1000, 2000], tipMin: 500, tipMax: 100000 },
};

// Map country codes to currencies
export const COUNTRY_TO_CURRENCY = {
  IN: 'INR', CA: 'CAD', US: 'USD', AE: 'AED', 
  AU: 'AUD', NZ: 'AUD', // New Zealand uses AUD as alternative
  GB: 'GBP', UK: 'GBP',
  SG: 'SGD', JP: 'JPY',
  // EU countries → EUR
  AT: 'EUR', BE: 'EUR', CY: 'EUR', EE: 'EUR', FI: 'EUR', FR: 'EUR',
  DE: 'EUR', GR: 'EUR', IE: 'EUR', IT: 'EUR', LV: 'EUR', LT: 'EUR',
  LU: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR', SK: 'EUR', SI: 'EUR',
  ES: 'EUR',
};

/**
 * Get user's currency based on country code or base currency
 * @param {Object} user - User object with country_code and/or base_currency
 * @returns {string} Currency code (e.g., 'CAD', 'USD', 'INR')
 */
export function getUserCurrency(user) {
  if (!user) return 'USD';
  
  // Prioritize country_code mapping
  if (user.country_code) {
    const detected = COUNTRY_TO_CURRENCY[user.country_code.toUpperCase()];
    if (detected) return detected;
  }
  
  // Fallback to base_currency
  if (user.base_currency) {
    const currency = user.base_currency.toUpperCase();
    if (CURRENCY_CONFIG[currency]) return currency;
  }
  
  return 'USD';
}

/**
 * Get formatted subscription price for user
 * @param {Object} user - User object
 * @returns {string} Formatted price (e.g., "C$6.95/mo", "₹149/mo")
 */
export function getSubscriptionPrice(user) {
  const currency = getUserCurrency(user);
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD;
  
  // Format price based on currency (whole numbers for INR/JPY, decimals for others)
  const price = currency === 'INR' || currency === 'JPY' 
    ? Math.round(config.subscription)
    : config.subscription.toFixed(2);
  
  return `${config.symbol}${price}/mo`;
}

/**
 * Get subscription price value only (no formatting)
 * @param {Object} user - User object
 * @returns {number} Price value
 */
export function getSubscriptionPriceValue(user) {
  const currency = getUserCurrency(user);
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD;
  return config.subscription;
}

/**
 * Get currency config for user
 * @param {Object} user - User object
 * @returns {Object} Currency configuration
 */
export function getCurrencyConfig(user) {
  const currency = getUserCurrency(user);
  return CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD;
}
